import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaGem, FaBoxOpen, FaCheckCircle, FaTruck, FaWarehouse, FaMoneyBillWave, FaCreditCard, FaMap, FaCheckDouble, FaSignOutAlt, FaChartLine, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { useOperator } from "../contexts/OperaotrContext.jsx";

// ── Sticky sidebar tuning ──────────────────────────────────────────────────
const SIDEBAR_WIDTH = 260; // expanded (labels visible)
const SIDEBAR_MIN_WIDTH = 72; // minimized (icons only)
const DESKTOP_QUERY = "(min-width: 992px)"; // sticky from here up
const COLLAPSED_STORAGE_KEY = "jmi-operator-sidebar-collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
  } catch (error) {
    return false;
  }
};

const styles = {
  container: {
    display: "flex",
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    backgroundColor: "#f4f6f8",
    minHeight: "100vh",
    color: "#343a40",
  },
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 999,
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.25s ease",
  },
  backdropVisible: { opacity: 1, pointerEvents: "auto" },
  drawer: {
    width: SIDEBAR_WIDTH,
    backgroundColor: "#ffffff",
    boxShadow: "4px 0 10px rgba(0,0,0,0.05)",
    transition:
      "transform 0.3s ease-in-out, width 0.25s ease-in-out, padding 0.25s ease-in-out, visibility 0.3s ease-in-out",
    transform: "translateX(-100%)",
    position: "fixed",
    top: 0,
    left: 0,
    height: "100%",
    zIndex: 1000,
    padding: "20px",
    boxSizing: "border-box",
    overflowY: "auto",
    overscrollBehavior: "contain",
    // Keeps the closed drawer out of the tab order / hit-testing.
    visibility: "hidden",
  },
  drawerOpen: { transform: "translateX(0)", visibility: "visible" },
  // Desktop: sidebar is pinned (sticky) and always on screen.
  drawerSticky: { transform: "translateX(0)", visibility: "visible" },
  // Desktop + minimized: icons-only rail.
  drawerMinimized: { width: SIDEBAR_MIN_WIDTH, padding: "20px 10px" },
  brandRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "30px",
  },
  logo: {
    flex: 1,
    textAlign: "center",
    fontSize: "22px",
    fontWeight: "bold",
    color: "#8a6b32",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontStyle: "italic",
    cursor: "pointer",
    lineHeight: 1.2,
  },
  brandMinimized: { textAlign: "center", marginBottom: "24px" },
  logoCompact: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#8a6b32",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontStyle: "italic",
    cursor: "pointer",
    marginBottom: "12px",
  },
  collapseButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    flexShrink: 0,
    padding: 0,
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#8a6b32",
    fontSize: "12px",
    cursor: "pointer",
  },
  navSection: { marginBottom: "25px" },
  navSectionMinimized: { marginBottom: "8px" },
  navDivider: { height: "1px", backgroundColor: "#e9ecef", margin: "6px 4px 10px" },
  navSectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#6c757d",
    textTransform: "uppercase",
    marginBottom: "10px",
    paddingLeft: "15px",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 15px",
    borderRadius: "8px",
    color: "#495057",
    textDecoration: "none",
    transition: "background-color 0.2s, color 0.2s",
    marginBottom: "5px",
  },
  navItemHover: {
    backgroundColor: "#f0e3d1",
    color: "#8a6b32",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  navItemActive: {
    backgroundColor: "#8a6b32",
    color: "#ffffff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  navItemMinimized: {
    justifyContent: "center",
    padding: "14px 0",
    minWidth: "48px",
    minHeight: "48px",
  },
  navItemIcon: {
    marginRight: "15px",
    fontSize: "18px",
    color: "#8a6b32",
    display: "flex",
    alignItems: "center",
  },
  navItemIconActive: { color: "#ffffff" },
  navItemIconMinimized: {
    marginRight: 0,
    fontSize: "20px",
    width: "24px",
    height: "24px",
  },
  mainContent: {
    flexGrow: 1,
    // Without this, wide tables (e.g. OrderRTD) force the whole page to
    // scroll horizontally every time the menu opens/closes.
    minWidth: 0,
    padding: "30px 40px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    gap: "12px",
  },
  menuButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    flexShrink: 0,
    fontSize: "24px",
    cursor: "pointer",
    color: "#8a6b32",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: 0,
    // Above the drawer (1000) and its backdrop (999) so the toggle is always
    // clickable — even while the menu is open.
    position: "relative",
    zIndex: 1100,
  },
  logoutButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    backgroundColor: "#8a6b32",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  logoutButtonHover: { backgroundColor: "#6f5527" },
};

const Layout = ({ children, pageTitle, title }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Minimized = icons-only rail (desktop only). Persisted between sessions.
  const [isMinimized, setIsMinimized] = useState(readCollapsed);
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(DESKTOP_QUERY).matches
  );
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isLogoutHovered, setIsLogoutHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { clearOperator } = useOperator();

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = () => setIsDrawerOpen((open) => !open);

  // ≥992px → sticky sidebar; below → overlay drawer.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (event) => setIsDesktop(event.matches);
    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Close the menu on every navigation (menu click, browser back/forward).
  // Without this the drawer stayed open and kept covering the page.
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  // Close the menu with the Escape key.
  useEffect(() => {
    if (!isDrawerOpen || isDesktop) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDrawerOpen, isDesktop]);

  const toggleMinimized = useCallback(() => {
    setIsMinimized((current) => {
      const next = !current;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? "1" : "0");
      } catch (error) {
        // localStorage unavailable — the state just won't persist.
      }
      return next;
    });
  }, []);

  // Header ☰: collapses/expands the sticky sidebar on desktop,
  // opens/closes the overlay drawer on mobile.
  const handleMenuClick = () => {
    if (isDesktop) toggleMinimized();
    else toggleDrawer();
  };

  const handleLogout = async () => {
    try {
      await logout();
      clearOperator();
      closeDrawer();
      navigate("/operatorlogin");
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Failed to logout. Please try again.");
    }
  };

  const navItems = {
    
    JRE: [
      { name: "JRE Approval", icon: <FaGem />, path: "/jreapproval" },
      { name: "JRE Live", icon: <FaMap />, path: "/jrelive" },
    ],
    Rates: [
      { name: "Live Rates", icon: <FaChartLine />, path: "/liverates" },
    ],
    ORDER: [
      { name: "Orders Received", icon: <FaBoxOpen />, path: "/ordersreceived" },
      { name: "Buyer Confirmation", icon: <FaCheckCircle />, path: "/ordersApproved" },
      { name: "Ready to Dispatch", icon: <FaTruck />, path: "/ordersRTD" },
      { name: "On the way", icon: <FaCheckDouble />, path: "/ordersOnTheWay" },
      { name: "In Warehouse", icon: <FaWarehouse />, path: "/inWarehouse" },
      { name: "Dispatched", icon: <FaTruck />, path: "/ordersDispatched" },
      { name: "Delivered", icon: <FaCheckCircle />, path: "/ordersDelivered" },
    ],
    Payments: [
      { name: "Payment Pending", icon: <FaMoneyBillWave />, path: "/paymentsPending" },
      { name: "Payment Received", icon: <FaCreditCard />, path: "/paymentsReceived" },
      { name: "Payment to be Paid", icon: <FaMoneyBillWave />, path: "/paymentsToBePaid" },
      ],
    RECONCILIATION:[
       { name: "RECONCILIATION", icon: <FaCreditCard />, path: "/paymentsPaid" },
     
    ]  
  };

  // Desktop + minimized: icons-only rail (labels hidden, tooltips shown).
  const showLabels = !(isDesktop && isMinimized);
  // Page modals use z-index ≥ 50, so the sticky sidebar sits *below* them;
  // the mobile overlay drawer stays above everything (1000).
  const drawerStyle = {
    ...styles.drawer,
    ...(isDesktop
      ? {
          ...styles.drawerSticky,
          ...(isMinimized ? styles.drawerMinimized : {}),
        }
      : isDrawerOpen
        ? styles.drawerOpen
        : {}),
    zIndex: isDesktop ? 40 : 1000,
  };

  return (
    <div style={styles.container}>
      {/* Click anywhere outside the drawer to close the menu (mobile only) */}
      <div
        onClick={closeDrawer}
        style={{
          ...styles.backdrop,
          ...(!isDesktop && isDrawerOpen ? styles.backdropVisible : {}),
        }}
        aria-hidden="true"
      />

      {/* Sidebar — sticky on desktop, overlay drawer on mobile */}
      <div
        id="jmi-operator-drawer"
        aria-hidden={isDesktop ? false : !isDrawerOpen}
        style={drawerStyle}
      >
        {isDesktop && isMinimized ? (
          <div style={styles.brandMinimized}>
            <div
              style={styles.logoCompact}
              title="JMI Operator — Home"
              onClick={() => {
                closeDrawer();
                navigate("/homepage");
              }}
            >
              JMI
            </div>
            <button
              type="button"
              onClick={toggleMinimized}
              title="Expand sidebar"
              aria-label="Expand sidebar"
              style={styles.collapseButton}
            >
              <FaChevronRight />
            </button>
          </div>
        ) : (
          <div style={styles.brandRow}>
            <div
              style={styles.logo}
              onClick={() => {
                closeDrawer();
                navigate("/homepage");
              }}
            >
              JMI Operator
            </div>
            {isDesktop && (
              <button
                type="button"
                onClick={toggleMinimized}
                title="Minimize sidebar"
                aria-label="Minimize sidebar"
                style={styles.collapseButton}
              >
                <FaChevronLeft />
              </button>
            )}
          </div>
        )}

        {Object.keys(navItems).map((sectionTitle) => (
          <div
            key={sectionTitle}
            style={{
              ...styles.navSection,
              ...(!showLabels ? styles.navSectionMinimized : {}),
            }}
          >
            {showLabels ? (
              <h3 style={styles.navSectionTitle}>{sectionTitle}</h3>
            ) : (
              <div style={styles.navDivider} />
            )}
            {navItems[sectionTitle].map((item) => {
              const isActive =
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={closeDrawer}
                  aria-current={isActive ? "page" : undefined}
                  title={showLabels ? undefined : item.name}
                  style={{
                    ...styles.navItem,
                    ...(!showLabels ? styles.navItemMinimized : {}),
                    ...(isActive
                      ? styles.navItemActive
                      : hoveredItem === item.name
                        ? styles.navItemHover
                        : {}),
                  }}
                  onMouseEnter={() => setHoveredItem(item.name)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div
                    style={{
                      ...styles.navItemIcon,
                      ...(!showLabels ? styles.navItemIconMinimized : {}),
                      ...(isActive ? styles.navItemIconActive : {}),
                    }}
                  >
                    {item.icon}
                  </div>
                  {showLabels && <div>{item.name}</div>}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div
        style={{
          ...styles.mainContent,
          ...(isDesktop
            ? {
                marginLeft: isMinimized
                  ? SIDEBAR_MIN_WIDTH
                  : SIDEBAR_WIDTH,
              }
            : {}),
        }}
      >
        <div style={styles.header}>
          <button
            type="button"
            onClick={handleMenuClick}
            aria-label={
              isDesktop
                ? isMinimized
                  ? "Expand sidebar"
                  : "Minimize sidebar"
                : "Toggle navigation menu"
            }
            aria-expanded={isDesktop ? !isMinimized : isDrawerOpen}
            aria-controls="jmi-operator-drawer"
            title={
              isDesktop
                ? isMinimized
                  ? "Expand sidebar"
                  : "Minimize sidebar"
                : "Toggle navigation menu"
            }
            style={styles.menuButton}
          >
            {!isDesktop && isDrawerOpen ? <FaTimes /> : <FaBars />}
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <h1 style={{ fontSize: "28px", color: "#8a6b32" }}>
              {pageTitle || title || "Dashboard"}
            </h1>
            <button
              onClick={handleLogout}
              style={{ ...styles.logoutButton, ...(isLogoutHovered ? styles.logoutButtonHover : {}) }}
              onMouseEnter={() => setIsLogoutHovered(true)}
              onMouseLeave={() => setIsLogoutHovered(false)}
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

export default Layout;
